import { useEffect, useState, useRef, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

interface Category {
  id: string;
  key: string;
  label: string;
  emoji: string | null;
  color: string;
  active: boolean;
  sort_order: number;
}

const COLOR_PRESETS = [
  "#D85A30", "#F2A623", "#1D9E75", "#534AB7",
  "#D4537E", "#639922", "#378ADD", "#888780",
];

export default function CategoryManager() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  // New category form
  const [newKey, setNewKey] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newEmoji, setNewEmoji] = useState("");
  const [newColor, setNewColor] = useState("#D85A30");

  useEffect(() => {
    loadCategories();
  }, []);

  async function loadCategories() {
    const { data, error } = await supabase
      .from("memory_categories")
      .select("id, key, label, emoji, color, active, sort_order")
      .order("sort_order");
    if (error) {
      toast.error("Failed to load categories");
      return;
    }
    setCategories(data ?? []);
    setLoading(false);
  }

  async function toggleActive(cat: Category) {
    const next = !cat.active;

    // If disabling, check usage count
    if (!next) {
      const { count } = await supabase
        .from("memories")
        .select("id", { count: "exact", head: true })
        .contains("categories", [cat.key]);

      if (count && count > 0) {
        const confirmed = window.confirm(
          `This category is used in ${count} memories. Disabling hides it from new memories but doesn't remove it from existing ones.\n\nContinue?`
        );
        if (!confirmed) return;
      }
    }

    setCategories((prev) => prev.map((c) => (c.id === cat.id ? { ...c, active: next } : c)));
    const { error } = await supabase
      .from("memory_categories")
      .update({ active: next })
      .eq("id", cat.id);
    if (error) {
      toast.error("Failed to toggle category");
      setCategories((prev) => prev.map((c) => (c.id === cat.id ? { ...c, active: !next } : c)));
    }
  }

  async function updateColor(cat: Category, color: string) {
    setCategories((prev) => prev.map((c) => (c.id === cat.id ? { ...c, color } : c)));
    const { error } = await supabase
      .from("memory_categories")
      .update({ color })
      .eq("id", cat.id);
    if (error) {
      toast.error("Failed to save color");
    }
  }

  async function addCategory() {
    if (!newKey.trim() || !newLabel.trim()) {
      toast.error("Key and label are required");
      return;
    }
    const maxSort = categories.reduce((m, c) => Math.max(m, c.sort_order), 0);
    const { data, error } = await supabase
      .from("memory_categories")
      .insert({
        key: newKey.trim(),
        label: newLabel.trim(),
        emoji: newEmoji.trim() || null,
        color: newColor,
        active: true,
        sort_order: maxSort + 1,
      })
      .select()
      .single();
    if (error) {
      toast.error("Failed to create category");
      return;
    }
    setCategories((prev) => [...prev, data]);
    setNewKey("");
    setNewLabel("");
    setNewEmoji("");
    setNewColor("#D85A30");
    setAdding(false);
    toast.success("Category added");
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-12">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl">
      <h1 className="text-xl font-bold text-gray-900">Memory Categories</h1>
      <p className="text-sm text-gray-500 mt-1 mb-6">
        Categories shown to users when tagging memories. Disabled categories are hidden from the UI.
      </p>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="admin-table">
          <thead>
            <tr>
              <th className="w-8"></th>
              <th>Color</th>
              <th>Emoji</th>
              <th>Key</th>
              <th>Label</th>
              <th>Sort</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => (
              <CategoryRow
                key={cat.id}
                category={cat}
                onToggle={() => toggleActive(cat)}
                onColorChange={(color) => updateColor(cat, color)}
                onUpdate={(updates) => {
                  setCategories((prev) =>
                    prev.map((c) => (c.id === cat.id ? { ...c, ...updates } : c))
                  );
                }}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Add category */}
      {adding ? (
        <div className="mt-4 bg-white rounded-lg border border-gray-200 p-5 space-y-3">
          <p className="text-sm font-semibold text-gray-900">New category</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Key (slug)
              </label>
              <input
                type="text"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
                placeholder="first_steps"
                className="admin-input font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Label
              </label>
              <input
                type="text"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="First Steps"
                className="admin-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Emoji
              </label>
              <input
                type="text"
                value={newEmoji}
                onChange={(e) => setNewEmoji(e.target.value)}
                placeholder="\uD83D\uDC63"
                className="admin-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newColor}
                  onChange={(e) => setNewColor(e.target.value)}
                  className="admin-input font-mono flex-1"
                />
                <div className="w-8 h-8 rounded-full border" style={{ backgroundColor: newColor }} />
              </div>
              <div className="flex gap-1.5 mt-2">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setNewColor(c)}
                    className={`w-5 h-5 rounded-full border transition-transform ${
                      newColor === c ? "ring-2 ring-offset-1 ring-orange-500 scale-110" : ""
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={addCategory}
              className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 transition-colors"
            >
              Create
            </button>
            <button
              onClick={() => setAdding(false)}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="mt-4 rounded-md border border-dashed border-gray-300 px-4 py-2.5 text-sm text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-colors w-full"
        >
          + Add category
        </button>
      )}
    </div>
  );
}

function CategoryRow({
  category,
  onToggle,
  onColorChange,
  onUpdate,
}: {
  category: Category;
  onToggle: () => void;
  onColorChange: (color: string) => void;
  onUpdate: (updates: Partial<Category>) => void;
}) {
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelVal, setLabelVal] = useState(category.label);
  const [sortVal, setSortVal] = useState(String(category.sort_order));
  const [showColorPicker, setShowColorPicker] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const debouncedSave = useCallback(
    (field: string, value: string | number) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        const { error } = await supabase
          .from("memory_categories")
          .update({ [field]: value } as never)
          .eq("id", category.id);
        if (error) toast.error(`Failed to save ${field}`);
      }, 800);
    },
    [category.id]
  );

  function handleLabelChange(val: string) {
    setLabelVal(val);
    onUpdate({ label: val });
    debouncedSave("label", val);
  }

  function handleSortChange(val: string) {
    setSortVal(val);
    const num = parseInt(val) || 0;
    onUpdate({ sort_order: num });
    debouncedSave("sort_order", num);
  }

  return (
    <tr>
      <td className="text-gray-300 cursor-grab select-none">{"\u22EE\u22EE"}</td>
      <td>
        <div className="relative">
          <button
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="w-6 h-6 rounded-full border border-gray-200"
            style={{ backgroundColor: category.color }}
          />
          {showColorPicker && (
            <div className="absolute top-8 left-0 z-20 bg-white rounded-lg shadow-lg border border-gray-200 p-3">
              <div className="flex gap-1.5 mb-2">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    onClick={() => {
                      onColorChange(c);
                      setShowColorPicker(false);
                    }}
                    className={`w-6 h-6 rounded-full border transition-transform ${
                      category.color === c ? "ring-2 ring-offset-1 ring-orange-500 scale-110" : ""
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
              <input
                type="text"
                defaultValue={category.color}
                onBlur={(e) => {
                  onColorChange(e.target.value);
                  setShowColorPicker(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    onColorChange((e.target as HTMLInputElement).value);
                    setShowColorPicker(false);
                  }
                }}
                className="w-full text-xs font-mono border rounded px-2 py-1"
                placeholder="#D85A30"
              />
            </div>
          )}
        </div>
      </td>
      <td className="text-lg">{category.emoji ?? "-"}</td>
      <td className="font-mono text-xs text-gray-600">{category.key}</td>
      <td>
        {editingLabel ? (
          <input
            autoFocus
            value={labelVal}
            onChange={(e) => handleLabelChange(e.target.value)}
            onBlur={() => setEditingLabel(false)}
            onKeyDown={(e) => e.key === "Enter" && setEditingLabel(false)}
            className="text-sm border rounded px-2 py-1 w-36 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        ) : (
          <button
            onClick={() => setEditingLabel(true)}
            className="text-sm text-gray-900 hover:text-orange-600 transition-colors"
          >
            {category.label}
          </button>
        )}
      </td>
      <td>
        <input
          type="number"
          value={sortVal}
          onChange={(e) => handleSortChange(e.target.value)}
          className="w-14 text-xs text-center border rounded px-1 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </td>
      <td>
        <Switch checked={category.active} onCheckedChange={onToggle} />
      </td>
    </tr>
  );
}
